import React, {
  FC,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  ServicesByDepartmentSectionProps,
  ServicesByDepartmentSection,
} from './services-by-department-section';
import { useCatalogByDepartmentSectionsByIdQuery } from '../../hooks/catalog-department-by-sections-ids-query';
import { useDepartmentSectionsByIdQuery } from '../../hooks/department-by-sections-ids-query';
import { useWebgate } from '@xvia/webgate-connect';
import { useRedirectWarningModal } from '../../hooks/use-redirect-warning-modal';
import { LoadingScreen } from '@plataformaxvia/design-system';
import { searchAddressCoordinates } from '../../apis/map';

const ITEMS_PER_PAGE = 8;

interface RouteParams {
  sectionId: string;
}

export const ServicesByDepartmentSectionContainer: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { sectionId } = useParams<RouteParams>();
  const { portalConnect } = useWebgate();

  const {
    isRedirectWarningModalOpen,
    toggleRedirectWarningModal,
    handleRedirectWarningModalConfirm,
    setCurrentServiceRedirectLink,
  } = useRedirectWarningModal();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const currentPage = Number(query.get('page')) || 1;
  const offset = (currentPage - 1) * ITEMS_PER_PAGE;

  const [mapUrl, setMapUrl] = useState('');

  const { data: section, isLoading: isSectionLoading } =
    useDepartmentSectionsByIdQuery({ sectionId });

  useEffect(() => {
    if (!section?.address) return;

    const { address, complement, district, city, state, postalCode } = section;
    const complementText = complement ? `, ${complement}` : '';

    const formattedAddress = `${address}${complementText}, ${district}, ${city} / ${state}, ${postalCode}`;

    searchAddressCoordinates(formattedAddress)
      .then((coordinates) => {
        if (coordinates.length === 0) {
          setMapUrl('');
          return;
        }

        const { lat, lon } = coordinates[0];

        setMapUrl(
          `https://www.google.com/maps?q=${lat},${lon}&z=15&output=embed`
        );
      })
      .catch(() => {
        console.log(
          'Não foi possível encontrar as informações geográficas do endereço.'
        );
      });
  }, [section]);

  const { data: catalogs, isLoading: isCatalogsLoading } =
    useCatalogByDepartmentSectionsByIdQuery({
      sectionId,
      limit: ITEMS_PER_PAGE,
      offset,
    });

  const services = useMemo(() => catalogs?.data ?? [], [catalogs]);

  const totalItems = catalogs?.resultTotal || 0;

  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  const updatePage = useCallback(
    (page: number) => {
      const url = new URL(window.location.href);

      url.searchParams.set('page', String(page));

      history.push(`${location.pathname}${url.search}`);
    },
    [history, location.pathname]
  );

  const handleInfoClick = useCallback(
    (slug: string) => {
      const pathname = `/carta-de-servicos/${slug}`;

      history.push(pathname);

      window.parent.dispatchEvent(
        new CustomEvent('catalog:navigation', {
          detail: {
            pathname: `/app/catalog${pathname}`,
            search: '',
            source: 'catalog',
          },
        })
      );
    },
    [history]
  );

  const handleRequestClick = useCallback(
    (link: string) => {
      if (!link.startsWith('/app')) {
        setCurrentServiceRedirectLink(link);
        toggleRedirectWarningModal();
        return;
      }
    
      let targetLink = link;
      if (section && link.startsWith('/app/agendamento-servicos')) {
        const params = new URLSearchParams();
        if (section.city) params.set('city', section.city);
        if (section.externalId) params.set('departmentSectionExternalId', section.externalId);
        const paramString = params.toString();
        if (paramString) {
          const separator = link.includes('?') ? '&' : '?';
          targetLink = `${link}${separator}${paramString}`;
        }
      }

      portalConnect?.redirectTo(targetLink);
    },
    [portalConnect, setCurrentServiceRedirectLink, toggleRedirectWarningModal, section]
  );

  const servicesByDepartmentSectionProps: ServicesByDepartmentSectionProps = {
    services: services.map((service) => ({
      title: service.name || '',
      onInfoClick: () => handleInfoClick(service.slug),
      onRequestClick: () => handleRequestClick(service.link),
      isOnline: service.isOnline && !!service.link,
      isDigital: service.isDigital && !!service.link,
      category: service?.category?.name || '',
      totalServices: service.resultTotal,
    })),
    setCurrentPage: updatePage,
    currentPage,
    totalPages,
    section: section ? { ...section, mapUrl } : undefined,
    isRedirectWarningModalOpen,
    toggleRedirectWarningModal,
    handleRedirectWarningModalConfirm,
  };

  if (isSectionLoading || isCatalogsLoading) {
    return createElement(LoadingScreen, {
      style: { minHeight: '750px' },
    });
  }

  return createElement(
    ServicesByDepartmentSection,
    servicesByDepartmentSectionProps
  );
};
